import { ReactElement, useState } from "react";
import {
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";

export interface EnrichmentFormProps {
  onClose?: () => void;
  onSubmit?: (data: EnrichmentData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface EnrichmentData {
  genes: string;
  file: File | null;
  usePrecomputed: boolean;
  idType: string;
  additionalParams?: Record<string, any>;
}

const EnrichmentForm = ({
  onClose,
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: EnrichmentFormProps): ReactElement => {
  const [formData, setFormData] = useState<EnrichmentData>({
    genes: "",
    file: null,
    usePrecomputed: true,
    idType: "ENSEMBL",
  });

  const exampleIds = `ENSG00000113492
ENSG00000182890
ENSG00000159423
ENSG00000086205
ENSG00000130707
ENSG00000136750
ENSG00000135821
ENSG00000203797
ENSG00000021826
ENSG00000185100
ENSG00000169154
ENSG00000167701
ENSG00000162174
ENSG00000112294
ENSG00000198380
ENSG00000128059
ENSG00000120053
ENSG00000185818
ENSG00000125166
ENSG00000166123`;

  const handleInputChange =
    (field: keyof EnrichmentData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any,
    ) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.type === "file"
            ? event.target.files?.[0] || null
            : event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      file,
    }));
  };

  const loadExampleData = () => {
    setFormData((prev) => ({
      ...prev,
      genes: exampleIds,
      file: null,
      usePrecomputed: false,
      idType: "ENSEMBL",
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit?.(formData);
  };

  return (
    <Box className={`flex flex-col h-full ${className}`}>
      <Box className="flex-1 p-6">
        <Typography variant="body1" className="mb-6 text-gray-600">
          Discover biological pathways, processes, and functions associated with your selected genes.
          pathways associated with your differentially expressed genes.
        </Typography>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <Box>
            <Typography variant="subtitle2" className="mb-2">
              Upload File
              <Tooltip title="Upload a file containing gene identifiers for enrichment analysis.">
                <InfoIcon className="ml-1 text-gray-400" fontSize="small" />
              </Tooltip>
            </Typography>
            <input
              type="file"
              onChange={handleFileChange}
              disabled={formData.usePrecomputed}
              className="w-full p-2 border border-gray-300 rounded bg-white disabled:bg-gray-100"
            />
          </Box>

          {/* Genes Text Area */}
          <Box>
            <Typography variant="subtitle2" className="mb-2">
              Enter Genes
              <Tooltip title="Enter a list of genes separated by newlines.">
                <InfoIcon className="ml-1 text-gray-400" fontSize="small" />
              </Tooltip>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={formData.genes}
              onChange={handleInputChange("genes")}
              disabled={formData.usePrecomputed}
              placeholder="Enter gene identifiers, one per line..."
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
            <Button
              type="button"
              variant="text"
              size="small"
              onClick={loadExampleData}
              className="mt-2 text-blue-600"
            >
              Load Example Data
            </Button>
          </Box>

          {/* Identifier Type */}
          <FormControl fullWidth>
            <InputLabel>Identifier Type</InputLabel>
            <Select
              value={formData.idType}
              onChange={handleInputChange("idType")}
              label="Identifier Type"
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="Entrez">Entrez</MenuItem>
              <MenuItem value="ENSEMBL">ENSEMBL</MenuItem>
              <MenuItem value="HGNC symbol">HGNC symbol</MenuItem>
              <MenuItem value="UNIPROT">UNIPROT</MenuItem>
            </Select>
            <Typography variant="caption" className="text-gray-500 mt-1">
              Select the type of gene identifiers you are using.
            </Typography>
          </FormControl>

          {/* Use Precomputed Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.usePrecomputed}
                onChange={handleInputChange("usePrecomputed")}
              />
            }
            label={
              <Box className="flex items-center">
                <Typography variant="body2">
                  Use precomputed cluster-specific marker genes from DE analysis
                </Typography>
                <Tooltip title="Enable this option to use precomputed marker genes instead of providing input.">
                  <InfoIcon className="ml-1 text-gray-400" fontSize="small" />
                </Tooltip>
              </Box>
            }
          />

          <Divider className="my-4" />
        </form>
      </Box>

      <Box className="flex justify-between items-center p-6 border-t">
        <Button variant="outlined" color="primary" onClick={onBack}>
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default EnrichmentForm;
